import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, FilePdf, House, Camera, Wrench } from "@phosphor-icons/react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const GRADE_COLORS = {
  A: "bg-emerald-500 text-white",
  B: "bg-lime-400 text-slate-900",
  C: "bg-amber-400 text-slate-900",
  D: "bg-orange-500 text-white",
  F: "bg-red-600 text-white",
};
const PRIORITY_COLORS = {
  high: "bg-red-100 text-red-700",
  med: "bg-amber-100 text-amber-700",
  low: "bg-slate-100 text-slate-600",
};
const CONDITION_COLORS = {
  GOOD: "bg-emerald-100 text-emerald-800",
  FAIR: "bg-amber-100 text-amber-800",
  POOR: "bg-red-100 text-red-800",
  NA: "bg-slate-100 text-slate-500",
};

export default function PublicInspectionReport() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/public/inspection/${token}`);
        setData(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "This inspection link isn't valid or has been revoked.");
      }
    })();
  }, [token]);

  if (err) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6" data-testid="public-inspection-error">
        <div className="max-w-md text-center bg-white rounded-2xl shadow-xl p-8">
          <div className="text-4xl mb-2">🔒</div>
          <div className="font-black text-lg text-[#0F1F3A]">Link unavailable</div>
          <div className="text-sm text-slate-500 mt-2">{err}</div>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-slate-100 flex items-center justify-center text-slate-500 font-black uppercase tracking-widest text-xs">Loading…</div>;
  }

  const completedWhen = data.completed_at
    ? new Date(data.completed_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
    : "";
  const pdfUrl = `${API}${data.pdf_url}`;
  const roomEntries = Object.entries(data.room_scores || {});
  const checklistEntries = Object.entries(data.checklist || {});

  return (
    <div className="min-h-screen bg-slate-100 pb-20" data-testid="public-inspection-page">
      {/* Header */}
      <header className="bg-[#0F1F3A] text-white">
        <div className="max-w-4xl mx-auto px-5 py-6">
          <div className="text-[10px] font-black uppercase tracking-widest text-[#00C4CC]">
            {data.company} · Property Inspection Report
          </div>
          <h1 className="text-2xl sm:text-3xl font-black mt-1" data-testid="public-inspection-title">{data.title}</h1>
          <div className="text-sm text-slate-300 mt-1 flex items-center gap-2">
            <House size={14} weight="bold"/>
            {data.address || "—"}{data.unit ? ` · Unit ${data.unit}` : ""}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500 text-white text-[10px] font-black uppercase tracking-widest">
              <CheckCircle size={12} weight="fill"/> Completed
              {data.report_version ? ` · v${data.report_version}` : ""}
            </span>
            {completedWhen && (
              <span className="text-[11px] text-slate-300">{completedWhen}</span>
            )}
            <a href={pdfUrl} target="_blank" rel="noreferrer"
              data-testid="public-inspection-download-pdf"
              className="ml-auto h-10 px-4 rounded-lg bg-[#00C4CC] hover:bg-[#00e5ed] text-[#0F1F3A] font-black uppercase tracking-widest text-[11px] inline-flex items-center gap-2">
              <FilePdf size={14} weight="fill"/> Download PDF
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-6 space-y-6">
        {/* Room condition scores */}
        {roomEntries.length > 0 && (
          <section className="bg-white rounded-2xl shadow-sm p-5" data-testid="public-inspection-scores">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3">Room condition scores</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {roomEntries.map(([room, s]) => {
                const grade = (typeof s === "object" ? s?.grade : s) || "—";
                const notes = (typeof s === "object" ? s?.notes : "") || "";
                return (
                  <div key={room} className="flex items-start gap-3 p-2 rounded-lg bg-slate-50">
                    <span className={`h-8 w-8 rounded-lg flex-shrink-0 flex items-center justify-center font-black text-sm ${GRADE_COLORS[(grade || "").toUpperCase()] || "bg-slate-200 text-slate-700"}`}>
                      {grade}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-black text-sm text-[#0F1F3A]">{room}</div>
                      {notes && <div className="text-[11px] text-slate-500 leading-snug mt-0.5">{notes}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Recommendations (sanitized) */}
        {data.recommendations?.length > 0 && (
          <section className="bg-white rounded-2xl shadow-sm p-5" data-testid="public-inspection-recs">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2">
              <Wrench size={12} weight="fill"/> Recommended work
            </div>
            <div className="space-y-2">
              {data.recommendations.map((r) => (
                <div key={r.id} className="flex items-start gap-3 p-3 rounded-lg border border-slate-100">
                  <span className={`h-6 px-2 rounded-full flex items-center text-[10px] font-black uppercase tracking-widest ${PRIORITY_COLORS[r.priority] || PRIORITY_COLORS.med}`}>
                    {r.priority || "med"}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-sm text-[#0F1F3A]">{r.action}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{(r.rooms || []).join(", ") || "—"}</div>
                    {r.why && <div className="text-[12px] text-slate-600 mt-1 leading-snug">{r.why}</div>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Checklist */}
        {checklistEntries.length > 0 && (
          <section className="bg-white rounded-2xl shadow-sm p-5" data-testid="public-inspection-checklist">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3">Room-by-room checklist</div>
            <div className="space-y-3">
              {checklistEntries.map(([room, items]) => (
                <details key={room} className="rounded-lg border border-slate-100 open:bg-slate-50">
                  <summary className="cursor-pointer px-3 py-2 font-black text-sm text-[#0F1F3A]">{room}</summary>
                  <div className="px-3 pb-3 space-y-1.5">
                    {Object.entries(items || {}).map(([item, cell]) => {
                      const cond = ((cell?.condition) || "").toUpperCase();
                      return (
                        <div key={item} className="flex items-start gap-2 text-[13px]">
                          <span className={`h-5 px-2 rounded-full flex items-center text-[9px] font-black uppercase tracking-widest ${CONDITION_COLORS[cond] || CONDITION_COLORS.NA}`}>
                            {cond || "n/a"}
                          </span>
                          <div className="flex-1">
                            <div className="text-slate-700">{item}</div>
                            {cell?.note && <div className="text-[11px] text-slate-500 mt-0.5">{cell.note}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* Photos */}
        {data.photos?.length > 0 && (
          <section className="bg-white rounded-2xl shadow-sm p-5" data-testid="public-inspection-photos">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2">
              <Camera size={12} weight="fill"/> Photo appendix · {data.photos.length}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {data.photos.map((p) => (
                <a key={p.file_id} href={`${API}${p.url}`} target="_blank" rel="noreferrer"
                  className="block rounded-lg overflow-hidden bg-slate-100 group">
                  <div className="aspect-square overflow-hidden">
                    <img src={`${API}${p.url}`} alt={p.caption || p.room || ""}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition"/>
                  </div>
                  {(p.room || p.caption) && (
                    <div className="p-1.5 text-[10px] text-slate-600 leading-tight">
                      {p.room && <div className="font-black text-[#0F1F3A]">{p.room}</div>}
                      {p.caption && <div className="truncate">{p.caption}</div>}
                    </div>
                  )}
                </a>
              ))}
            </div>
          </section>
        )}

        <div className="text-center text-[10px] text-slate-400 pt-2">
          Powered by Field Radar · this link stays private to whoever has it
        </div>
      </main>
    </div>
  );
}
